import React, { useState } from "react";
import axios from "axios";
import baseUrl from "../api/utils";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

// Maps UI labels to the numeric codes Sandbox's E-Way Bill API expects
const TRANSPORT_MODES = [
  { label: "Road", value: "1" },
  { label: "Rail", value: "2" },
  { label: "Air", value: "3" },
  { label: "Ship", value: "4" },
];

const VEHICLE_TYPES = [
  { label: "Regular", value: "R" },
  { label: "Over Dimensional Cargo (ODC)", value: "O" },
];

// ═══════════════════════════════════════════════════════════════════════════
// GENERATE E-WAY BILL — opened from Invoice view after a sale is saved.
// Collects transporter/vehicle details, posts to our backend, which relays
// to Sandbox using the credentials saved via EwayBillSettingsForm.
// ═══════════════════════════════════════════════════════════════════════════
const GenerateEwayBillModal = ({ saleInvoiceId, invoiceNumber, grandTotal, onClose, onGenerated }) => {
  const MySwal = withReactContent(Swal);
  const token = sessionStorage.getItem("token");

  const [transportMode, setTransportMode] = useState("1"); // default: Road
  const [vehicleType, setVehicleType] = useState("R");
  const [transporterName, setTransporterName] = useState("");
  const [transporterId, setTransporterId] = useState("");
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [buyerPincode, setBuyerPincode] = useState("");
  const [distanceKm, setDistanceKm] = useState("");

  const [errors, setErrors] = useState({});
  const [generating, setGenerating] = useState(false);

  const isRoad = transportMode === "1";

  const validate = () => {
    const e = {};
    if (isRoad && !vehicleNumber.trim()) e.vehicleNumber = "Vehicle number is required for Road transport";
    if (!buyerPincode.trim() || buyerPincode.trim().length !== 6) e.buyerPincode = "Enter a valid 6-digit pincode";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleGenerate = async () => {
    if (!validate()) return;

    setGenerating(true);
    try {
      const payload = {
        saleInvoiceId,
        vehicleNumber: isRoad ? vehicleNumber.toUpperCase() : null,
        vehicleType,
        transportMode,
        transporterName: transporterName || null,
        transporterId: transporterId || null,
        buyerPincode,
        distanceKm: distanceKm ? parseFloat(distanceKm) : null, // Sandbox auto-calculates if left blank
      };

      const res = await axios.post(`${baseUrl}/api/eway-bill/generate`, payload, {
        headers: { Authorization: token },
      });

      MySwal.fire({
        icon: "success",
        title: "E-Way Bill Generated",
        html: `EBN No: <b>${res.data.ewbNumber}</b><br/>Valid Until: <b>${res.data.validUntil ?? "-"}</b>`,
        confirmButtonText: "OK",
      }).then(() => {
        onGenerated?.(res.data);
        onClose?.();
      });
    } catch (err) {
      MySwal.fire({
        icon: "error",
        title: "Generation Failed",
        text: err.response?.data || "Could not generate the E-Way Bill. Please check the details and try again.",
      });
    } finally {
      setGenerating(false);
    }
  };

  return (
     
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 99999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "8px",
          width: "560px",
          maxWidth: "90vw",
          maxHeight: "85vh",
          overflowY: "auto",
          boxShadow: "0 8px 40px rgba(0,0,0,0.3)",
        }}
      >

             <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 24px", borderBottom: "1px solid #eee" }}>
            <h5 className="modal-title">
              Generate E-Way Bill {invoiceNumber ? `— #${invoiceNumber}` : ""}
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

                 <div style={{ padding: "20px 24px" }}>
            {grandTotal != null && (
              <p className="text-muted mb-3">
                Invoice Value: <b>₹{Number(grandTotal).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</b>
              </p>
            )}

            {/* Transport Mode */}
            <div className="form-group row">
              <label className="col-sm-3 col-form-label">Transport Mode <span className="text-danger">*</span></label>
              <div className="col-sm-9">
                <select className="form-control" value={transportMode} onChange={(e) => setTransportMode(e.target.value)}>
                  {TRANSPORT_MODES.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Vehicle Type */}
            <div className="form-group row">
              <label className="col-sm-3 col-form-label">Vehicle Type</label>
              <div className="col-sm-9">
                <select className="form-control" value={vehicleType} onChange={(e) => setVehicleType(e.target.value)}>
                  {VEHICLE_TYPES.map((v) => (
                    <option key={v.value} value={v.value}>{v.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Vehicle Number — only for Road */}
            {isRoad && (
              <div className="form-group row">
                <label className="col-sm-3 col-form-label">Vehicle Number <span className="text-danger">*</span></label>
                <div className="col-sm-9">
                  <input
                    type="text"
                    className={`form-control ${errors.vehicleNumber ? 'is-invalid' : ''}`}
                    value={vehicleNumber}
                    onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())}
                    placeholder="e.g. TN59AB1234"
                  />
                  {errors.vehicleNumber && <div className="invalid-feedback">{errors.vehicleNumber}</div>}
                </div>
              </div>
            )}

            {/* Transporter Name */}
            <div className="form-group row">
              <label className="col-sm-3 col-form-label">Transporter Name</label>
              <div className="col-sm-9">
                <input
                  type="text"
                  className="form-control"
                  value={transporterName}
                  onChange={(e) => setTransporterName(e.target.value)}
                  placeholder="Optional"
                />
              </div>
            </div>

            {/* Transporter GSTIN/ID */}
            <div className="form-group row">
              <label className="col-sm-3 col-form-label">Transporter ID</label>
              <div className="col-sm-9">
                <input
                  type="text"
                  className="form-control"
                  value={transporterId}
                  onChange={(e) => setTransporterId(e.target.value)}
                  placeholder="Optional — Transporter GSTIN"
                />
              </div>
            </div>

            {/* Buyer Pincode */}
            <div className="form-group row">
              <label className="col-sm-3 col-form-label">Buyer Pincode <span className="text-danger">*</span></label>
              <div className="col-sm-9">
                <input
                  type="text"
                  maxLength={6}
                  className={`form-control ${errors.buyerPincode ? 'is-invalid' : ''}`}
                  value={buyerPincode}
                  onChange={(e) => setBuyerPincode(e.target.value.replace(/\D/g, ''))}
                  placeholder="6-digit delivery pincode"
                />
                {errors.buyerPincode && <div className="invalid-feedback">{errors.buyerPincode}</div>}
              </div>
            </div>

            {/* Distance (optional) */}
            <div className="form-group row">
              <label className="col-sm-3 col-form-label">Distance (km)</label>
              <div className="col-sm-9">
                <input
                  type="number"
                  className="form-control"
                  value={distanceKm}
                  onChange={(e) => setDistanceKm(e.target.value)}
                  placeholder="Optional — auto-calculated if left blank"
                />
              </div>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", padding: "14px 24px", borderTop: "1px solid #eee" }}>
            <span onClick={onClose} style={{ cursor: "pointer", fontSize: "18px", color: "#888" }}>✕</span>
            <button className="btn btn-primary" onClick={handleGenerate} disabled={generating}>
              {generating ? "Generating..." : "Generate"}
            </button>
          </div>

        </div>
    </div>
  );
};

export default GenerateEwayBillModal;